import { useState, useEffect, useCallback } from 'react'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'

export interface SchoolMembership {
  schoolId: string
  schoolName: string
  relationshipType: 'employee' | 'contractor' | null
  hourlyRate: number | null
  address: string | null
  phone: string | null
  email: string | null
}

/**
 * The registered DriveTrack schools this instructor actively belongs to (employee or
 * contractor — public.school_memberships, joined to public.schools for the display
 * name). Distinct from useInstructorExternalSchools, which lists the instructor's own
 * PRIVATE labels for schools that are not DriveTrack tenants at all. Used by Enroll.tsx
 * to offer a real schoolId attribution (migration 065's send-invite extension) alongside
 * the existing externalSchoolId choices.
 */
export function useInstructorSchoolMemberships() {
  const { user } = useAuth()
  const [schools, setSchools] = useState<SchoolMembership[]>([])
  const [loading, setLoading] = useState(true)

  const fetchMemberships = useCallback(async () => {
    if (!user) return
    setLoading(true)
    try {
      const { data } = await supabase
        .from('school_memberships')
        .select('school_id, relationship_type, hourly_rate, schools(name, address, phone, business_email)')
        .eq('user_id', user.id)
        .eq('status', 'active')
      setSchools((data ?? []).map((row: any) => ({
        schoolId: row.school_id,
        schoolName: row.schools?.name ?? 'School',
        relationshipType: row.relationship_type ?? null,
        hourlyRate: row.hourly_rate === null || row.hourly_rate === undefined ? null : Number(row.hourly_rate),
        address: row.schools?.address ?? null,
        phone: row.schools?.phone ?? null,
        email: row.schools?.business_email ?? null,
      })))
    } finally {
      setLoading(false)
    }
  }, [user])

  useEffect(() => { fetchMemberships() }, [fetchMemberships])

  return { schools, loading, refresh: fetchMemberships }
}
